import { CombinedState, combineReducers, configureStore } from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/query'
import { FLUSH, PAUSE, PERSIST, PURGE, REGISTER, REHYDRATE, persistReducer, persistStore } from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import { useDispatch } from 'react-redux'

import { enhancedApi as webApi } from '../../common/redux/api/enhancedWebApi'
import companyReducer from '../../common/redux/features/company/companySlice'
import tokenReducer from '../../common/redux/features/token/tokenSlice'
import userReducer, { reset } from '../../common/redux/features/user/userSlice'

const createRootReducers = () => {
    const rootReducerFn = combineReducers({
        company: companyReducer,
        user: userReducer,
        token: tokenReducer,
        [webApi.reducerPath]: webApi.reducer,
    })

    return (state: Parameters<typeof rootReducerFn>[0], action: Parameters<typeof rootReducerFn>[1]) => {
        return rootReducerFn(reset && action.type === reset.type ? undefined : state, action)
    }
}

const persistConfig = {
    key: 'root',
    version: 1,
    storage,
    whitelist: ['user', 'token'],
}

const persistedReducer = persistReducer(persistConfig, createRootReducers())

export const store = configureStore({
    reducer: persistedReducer,
    devTools: process.env.NODE_ENV === 'development',
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
            },
        }).concat([webApi.middleware]),
})

export const preloadedStoreFactory = (preloadedState: CombinedState<unknown>) => {
    return configureStore({
        reducer: createRootReducers(),
        preloadedState,
        devTools: process.env.NODE_ENV === 'development',
        middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat([webApi.middleware]),
    })
}

export const persistor = persistStore(store)

setupListeners(store.dispatch)

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch

export const useAppDispatch = () => useDispatch<AppDispatch>()
